/**
 * Status Icons
 * 
 * Icons representing different status states like success, error, warning, info.
 * These are commonly used in alerts, toasts, and status indicators.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { getIconClasses } from '../iconUtils';

const iconPropTypes = {
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'warning', 'danger', 'neutral', 'white']),
  className: PropTypes.string,
};

/**
 * SuccessIcon - Checkmark in circle for success states
 */
export const SuccessIcon = ({ size = 'md', color = 'success', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

SuccessIcon.propTypes = iconPropTypes;

/**
 * ErrorIcon - X in circle for error states
 */
export const ErrorIcon = ({ size = 'md', color = 'danger', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

ErrorIcon.propTypes = iconPropTypes;

/**
 * WarningIcon - Exclamation mark in triangle for warnings
 */
export const WarningIcon = ({ size = 'md', color = 'warning', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

WarningIcon.propTypes = iconPropTypes;

/**
 * InfoIcon - i in circle for information
 */
export const InfoIcon = ({ size = 'md', color = 'primary', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

InfoIcon.propTypes = iconPropTypes;

/**
 * AlertIcon - Exclamation in circle for alerts
 */
export const AlertIcon = ({ size = 'md', color = 'danger', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

AlertIcon.propTypes = iconPropTypes;
